<!-- assignments.vue - CLEAN MODERN DESIGN -->
<template>
  <main class="flex-grow container mx-auto p-4 sm:p-6">
    <div class="text-center">
      <!-- Week Navigation - Cleaner -->
      <div class="flex flex-col sm:flex-row justify-between items-center mb-8 gap-4">
        <button
            @click="previousWeek"
            class="group bg-slate-800 hover:bg-slate-700 dark:bg-slate-700 dark:hover:bg-slate-600 text-white font-semibold py-2.5 px-5 rounded-xl shadow-md hover:shadow-lg transform hover:-translate-x-0.5 transition-all duration-200"
        >
          <span class="flex items-center gap-2">
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7"/>
            </svg>
            Previous
          </span>
        </button>
        <div class="text-center">
          <h3 class="text-3xl sm:text-4xl font-bold text-slate-900 dark:text-white">
            Week of {{ formatDate(currentWeekStart) }}
          </h3>
        </div>
        <button
            @click="nextWeek"
            class="group bg-slate-800 hover:bg-slate-700 dark:bg-slate-700 dark:hover:bg-slate-600 text-white font-semibold py-2.5 px-5 rounded-xl shadow-md hover:shadow-lg transform hover:translate-x-0.5 transition-all duration-200"
        >
          <span class="flex items-center gap-2">
            Next
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/>
            </svg>
          </span>
        </button>
      </div>

      <!-- View Toggle - Cleaner Pills -->
      <div class="flex justify-center mb-8">
        <div class="bg-slate-100 dark:bg-slate-800 p-1 rounded-xl inline-flex">
          <button
              @click="isWeekView = true"
              :class="[
              isWeekView
                ? 'bg-slate-700 dark:bg-indigo-600 text-white shadow-md'
                : 'text-slate-600 dark:text-slate-400',
              'font-semibold py-2 px-6 rounded-lg transition-all duration-200'
            ]"
          >
            Week View
          </button>
          <button
              @click="isWeekView = false"
              :class="[
              !isWeekView
                ? 'bg-slate-700 dark:bg-indigo-600 text-white shadow-md'
                : 'text-slate-600 dark:text-slate-400',
              'font-semibold py-2 px-6 rounded-lg transition-all duration-200'
            ]"
          >
            Day View
          </button>
        </div>
      </div>

      <!-- Day Navigation - Simplified -->
      <div v-if="!isWeekView" class="flex flex-wrap justify-center mb-8 gap-2">
        <button
            v-for="(day, index) in daysOfWeek"
            :key="index"
            @click="currentDayIndex = index"
            :class="[
            currentDayIndex === index
              ? 'bg-slate-700 dark:bg-indigo-600 text-white shadow-md'
              : 'bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-300 border border-slate-200 dark:border-slate-700',
            'font-semibold py-2 px-5 rounded-lg transition-all duration-200 hover:shadow-md'
          ]"
        >
          {{ day }}
        </button>
      </div>

      <!-- Loading Spinner -->
      <div v-if="isLoading" class="flex justify-center items-center h-64">
        <div class="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-slate-600 dark:border-indigo-600"></div>
      </div>

      <!-- Assignment List - Cleaner Container -->
      <div
          v-else
          class="bg-white/80 dark:bg-slate-900/80 backdrop-blur-sm shadow-xl rounded-2xl p-6 sm:p-8 border border-slate-200/50 dark:border-slate-700/50"
      >
        <h4 class="text-2xl sm:text-3xl font-bold mb-8 text-slate-900 dark:text-white">
          {{ isWeekView ? "Week's Assignments" : daysOfWeek[currentDayIndex] + "'s Assignments" }}
        </h4>

        <!-- Week View - Cleaner Cards -->
        <div v-if="isWeekView" class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
          <div
              v-for="(assignments, day) in weeklyAssignments"
              :key="day"
              class="flex flex-col"
          >
            <div class="bg-slate-700 dark:bg-indigo-600 text-white font-bold text-base py-2.5 px-4 rounded-t-xl">
              {{ day }}
            </div>
            <div class="bg-white dark:bg-slate-800 rounded-b-xl border-x border-b border-slate-200 dark:border-slate-700 min-h-[200px] p-3">
              <ul v-if="assignments.length" class="space-y-2.5">
                <li
                    v-for="assignment in assignments"
                    :key="assignment.assignment_id"
                    class="bg-slate-50 dark:bg-slate-900 hover:bg-white dark:hover:bg-slate-800 rounded-lg p-3 shadow-sm hover:shadow-md transition-all duration-200 cursor-pointer border border-slate-200 dark:border-slate-700"
                    @click="openModal(assignment)"
                >
                  <div class="flex flex-col">
                    <div class="flex-1 mb-2.5">
                      <h5 class="text-sm font-bold mb-1 text-slate-900 dark:text-white">
                        {{ assignment.section_name }}
                      </h5>
                      <p class="text-slate-600 dark:text-slate-400 text-xs leading-relaxed">
                        {{ truncateText(assignment.short_description, 50) }}
                      </p>
                    </div>
                    <!-- Status Indicators - Simplified -->
                    <div class="flex gap-1.5 justify-center pt-2">
                      <button
                          v-for="status in statuses"
                          :key="status.value"
                          @click.stop="updateStatus(assignment, status.value)"
                          :class="[
                          'relative group/status flex items-center justify-center w-7 h-7 rounded-lg transition-all duration-200',
                          assignment.status === status.value
                            ? `${status.bgClass} text-white`
                            : 'bg-slate-200 dark:bg-slate-700 text-slate-400 hover:bg-slate-300 dark:hover:bg-slate-600'
                        ]"
                      >
                        <!-- Not Started -->
                        <svg
                            v-if="status.value === 'not-started'"
                            class="w-4 h-4"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                        >
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                        <!-- In Progress -->
                        <svg
                            v-else-if="status.value === 'in-progress'"
                            class="w-4 h-4"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                        >
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <!-- Complete -->
                        <svg
                            v-else-if="status.value === 'complete'"
                            class="w-4 h-4"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                        >
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M5 13l4 4L19 7" />
                        </svg>
                        <!-- Tooltip -->
                        <span class="absolute bottom-full mb-2 w-max bg-slate-900 text-white text-xs rounded-lg py-1 px-2.5 opacity-0 group-hover/status:opacity-100 transition-opacity duration-200 pointer-events-none">
                          {{ status.label }}
                        </span>
                      </button>
                    </div>
                  </div>
                </li>
              </ul>
              <div v-else class="flex items-center justify-center h-full text-slate-500 dark:text-slate-400">
                <div class="text-center py-8">
                  <svg class="w-10 h-10 mx-auto mb-2 opacity-30" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <p class="text-xs">No assignments</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Day View - Cleaner Cards -->
        <div v-else>
          <ul v-if="dailyAssignments.length" class="space-y-4">
            <li
                v-for="assignment in dailyAssignments"
                :key="assignment.assignment_id"
                class="bg-white dark:bg-slate-800 hover:shadow-lg rounded-xl p-5 shadow-md transition-all duration-200 cursor-pointer border border-slate-200 dark:border-slate-700"
                @click="openModal(assignment)"
            >
              <div class="flex flex-col sm:flex-row justify-between items-start gap-4">
                <div class="flex-1 w-full">
                  <div class="flex items-start gap-3 mb-3">
                    <div class="w-1 h-12 bg-slate-600 dark:bg-indigo-600 rounded-full flex-shrink-0"></div>
                    <div class="flex-1">
                      <h5 class="text-lg font-bold mb-1.5 text-slate-900 dark:text-white">
                        {{ assignment.section_name }}
                      </h5>
                      <p class="text-slate-600 dark:text-slate-300 text-sm leading-relaxed">
                        {{ truncateText(assignment.short_description, 100) }}
                        <span
                            v-if="assignment.short_description.length > 100"
                            @click.stop="openModal(assignment)"
                            class="text-slate-700 dark:text-indigo-400 hover:text-slate-900 dark:hover:text-indigo-300 font-semibold cursor-pointer ml-1 transition-colors"
                        >
                          Read More →
                        </span>
                      </p>
                    </div>
                  </div>
                </div>
                <div class="flex flex-col items-end gap-3 sm:min-w-[220px]">
                  <div class="flex items-center gap-2 text-slate-500 dark:text-slate-400 text-sm">
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    <span class="font-medium">{{ formatDate(assignment.date_due) }}</span>
                  </div>
                  <!-- Status Pills - Cleaner -->
                  <div class="flex gap-2">
                    <button
                        v-for="status in statuses"
                        :key="status.value"
                        @click.stop="updateStatus(assignment, status.value)"
                        :class="[
                        'px-3 py-1.5 rounded-lg text-xs font-semibold transition-all duration-200',
                        assignment.status === status.value
                          ? `${status.bgClass} text-white shadow-sm`
                          : 'bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-600'
                      ]"
                    >
                      {{ status.label }}
                    </button>
                  </div>
                  <!-- Status Counts -->
                  <p v-if="assignment.status_counts && assignment.status_counts['complete']" class="text-xs text-slate-500 dark:text-slate-400 flex items-center gap-1.5">
                    <svg class="w-3.5 h-3.5 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                      <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd" />
                    </svg>
                    <span>
                      {{ assignment.status_counts['complete'] }}
                      {{ pluralize(assignment.status_counts['complete'], 'person', 'people') }} completed
                    </span>
                  </p>
                  <!-- Delete Button -->
                  <button
                      v-if="assignment.user_task"
                      @click.stop="deleteAssignment(assignment)"
                      class="text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 font-semibold text-xs flex items-center gap-1 transition-colors"
                  >
                    <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                    Delete
                  </button>
                </div>
              </div>
            </li>
          </ul>
          <div v-else class="text-center py-16">
            <svg class="w-16 h-16 mx-auto mb-3 text-slate-300 dark:text-slate-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <p class="text-slate-500 dark:text-slate-400">No assignments due on this day</p>
          </div>
        </div>

        <!-- Add Custom Task Button - Cleaner -->
        <button
            @click="openAddTaskModal"
            class="mt-8 bg-emerald-600 hover:bg-emerald-700 text-white px-6 py-3 rounded-xl font-semibold shadow-md hover:shadow-lg transition-all duration-200 flex items-center gap-2 mx-auto"
        >
          <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
          </svg>
          Add Custom Task
        </button>
      </div>
    </div>

    <!-- Modal for Assignment Description - Cleaner -->
    <div
        v-if="showModal"
        class="fixed inset-0 flex items-center justify-center z-50 bg-black/50 backdrop-blur-sm p-4"
        @wheel.stop
    >
      <div
          class="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl p-8 max-w-2xl w-full relative border border-slate-200 dark:border-slate-700 max-h-[85vh] overflow-hidden"
          @click.stop
          @touchmove.stop
      >
        <button
            @click="closeModal"
            class="absolute top-6 right-6 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 transition-colors"
        >
          <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>

        <h3 class="text-2xl font-bold mb-1 text-slate-900 dark:text-white break-words pr-12">
          {{ currentAssignment.section_name }}
        </h3>
        <p class="text-base font-semibold text-slate-500 dark:text-slate-400 mb-6">
          {{ currentAssignment.short_description }}
        </p>

        <!-- Scrollable description -->
        <div
            class="desc-scroll text-slate-700 dark:text-slate-300 whitespace-pre-wrap break-words max-h-[45vh] overflow-y-auto pr-2 leading-relaxed"
        >
          {{ currentAssignment.long_description }}
        </div>

        <!-- Due date with class start time -->
        <div class="flex items-center gap-2 text-slate-600 dark:text-slate-400 mt-6">
          <svg class="w-5 h-5 text-indigo-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"></path>
          </svg>
          <span class="text-base">
            Due: {{ formatModalDueDate(currentAssignment.date_due) }}
            <span v-if="currentAssignment.classStartTime" class="text-slate-500 dark:text-slate-500">
              (class at {{ currentAssignment.classStartTime }})
            </span>
          </span>
        </div>

        <div class="flex justify-end gap-3 mt-6 pt-6 border-t border-slate-200 dark:border-slate-700">
          <button
              v-if="currentAssignment.user_task"
              @click="deleteAssignment(currentAssignment); closeModal()"
              class="bg-red-600 hover:bg-red-700 text-white px-5 py-2.5 rounded-xl font-semibold shadow-md hover:shadow-lg transition-all duration-200"
          >
            Delete
          </button>
          <button
              @click="closeModal"
              class="bg-slate-700 hover:bg-slate-800 dark:bg-indigo-600 dark:hover:bg-indigo-700 text-white px-6 py-2.5 rounded-xl font-semibold shadow-md hover:shadow-lg transition-all duration-200"
          >
            Close
          </button>
        </div>
      </div>
    </div>

    <!-- Modal for Adding Custom Task - With Live Preview -->
    <div
        v-if="showAddTaskModal"
        class="fixed inset-0 flex items-center justify-center z-50 bg-black/50 backdrop-blur-sm p-4"
        @wheel.stop
        @touchmove.prevent
    >
      <div class="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl p-8 max-w-5xl w-full border border-slate-200 dark:border-slate-700 max-h-[90vh] overflow-y-auto" @click.stop>
        <h3 class="text-2xl font-bold mb-2 text-slate-900 dark:text-white">Add Custom Task</h3>
        <p class="text-sm text-slate-500 dark:text-slate-400 mb-6">Fill in at least one field below</p>

        <!-- Two Column Layout -->
        <div class="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <!-- Left Column: Form -->
          <div>
            <form @submit.prevent="addTask">
              <div class="mb-4">
                <label for="section" class="block text-slate-700 dark:text-slate-300 mb-2 font-medium text-sm">
                  Section <span class="text-slate-400 text-xs">(optional)</span>
                </label>
                <input
                    v-model="newTask.section_name"
                    id="section"
                    type="text"
                    placeholder="Custom Assignment"
                    class="w-full px-4 py-2.5 bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-700 rounded-lg text-slate-900 dark:text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-500 dark:focus:ring-indigo-500 focus:border-transparent transition-all"
                />
              </div>
              <div class="mb-4">
                <label for="title" class="block text-slate-700 dark:text-slate-300 mb-2 font-medium text-sm">
                  Title <span class="text-slate-400 text-xs">(optional)</span>
                </label>
                <input
                    v-model="newTask.short_description"
                    id="title"
                    type="text"
                    placeholder="Custom Assignment"
                    class="w-full px-4 py-2.5 bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-700 rounded-lg text-slate-900 dark:text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-500 dark:focus:ring-indigo-500 focus:border-transparent transition-all"
                />
              </div>
              <div class="mb-4 relative">
                <label class="block text-slate-700 dark:text-slate-300 mb-2 font-medium text-sm">
                  Due Date <span class="text-red-500">*</span>
                </label>
                <div
                    @click="toggleDatePicker"
                    class="w-full px-4 py-2.5 bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-700 rounded-lg text-slate-900 dark:text-white cursor-pointer hover:border-slate-400 dark:hover:border-slate-600 transition-all flex items-center justify-between"
                    :class="{ 'ring-2 ring-slate-500 dark:ring-indigo-500 border-transparent': showDatePicker }"
                >
                  <span :class="{ 'text-slate-400': !newTask.date_due }">
                    {{ newTask.date_due ? formatDate(newTask.date_due) : 'Select a date' }}
                  </span>
                  <svg class="w-5 h-5 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                </div>

                <!-- Custom Date Picker Dropdown -->
                <div
                    v-if="showDatePicker"
                    class="absolute z-50 mt-2 bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-700 rounded-xl shadow-2xl p-4 w-full"
                    @click.stop
                >
                  <!-- Month Navigation -->
                  <div class="flex items-center justify-between mb-4">
                    <button
                        @click="previousMonth"
                        type="button"
                        class="p-2 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
                    >
                      <svg class="w-5 h-5 text-slate-600 dark:text-slate-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
                      </svg>
                    </button>
                    <h4 class="text-sm font-bold text-slate-900 dark:text-white">
                      {{ currentMonthYear }}
                    </h4>
                    <button
                        @click="nextMonth"
                        type="button"
                        class="p-2 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
                    >
                      <svg class="w-5 h-5 text-slate-600 dark:text-slate-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
                      </svg>
                    </button>
                  </div>

                  <!-- Day Headers -->
                  <div class="grid grid-cols-7 gap-1 mb-2">
                    <div
                        v-for="day in ['M', 'T', 'W', 'T', 'F', 'S', 'S']"
                        :key="day"
                        class="text-center text-xs font-semibold text-slate-500 dark:text-slate-400 py-1"
                    >
                      {{ day }}
                    </div>
                  </div>

                  <!-- Calendar Days -->
                  <div class="grid grid-cols-7 gap-1">
                    <button
                        v-for="(date, index) in calendarDays"
                        :key="index"
                        @click="selectDate(date)"
                        type="button"
                        :disabled="!date || isDateDisabled(date)"
                        :class="[
                          'aspect-square rounded-lg text-sm font-medium transition-all',
                          !date ? 'invisible' : '',
                          date && !isDateDisabled(date)
                            ? 'hover:bg-slate-100 dark:hover:bg-slate-700 cursor-pointer'
                            : '',
                          date && isDateDisabled(date)
                            ? 'text-slate-300 dark:text-slate-600 cursor-not-allowed line-through'
                            : 'text-slate-700 dark:text-slate-200',
                          isDateSelected(date)
                            ? 'bg-indigo-600 dark:bg-indigo-500 text-white hover:bg-indigo-700 dark:hover:bg-indigo-600'
                            : ''
                        ]"
                    >
                      {{ date ? date.getDate() : '' }}
                    </button>
                  </div>

                  <!-- Helper Text -->
                  <div class="mt-3 pt-3 border-t border-slate-200 dark:border-slate-700">
                    <p class="text-xs text-slate-500 dark:text-slate-400 text-center">
                      Weekends and past dates are disabled
                    </p>
                  </div>
                </div>
              </div>
              <div class="mb-6">
                <label for="description" class="block text-slate-700 dark:text-slate-300 mb-2 font-medium text-sm">
                  Description <span class="text-slate-400 text-xs">(optional)</span>
                </label>
                <textarea
                    v-model="newTask.long_description"
                    id="description"
                    placeholder="Add any additional details..."
                    class="w-full px-4 py-2.5 bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-700 rounded-lg text-slate-900 dark:text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-500 dark:focus:ring-indigo-500 focus:border-transparent transition-all resize-none"
                    rows="4"
                ></textarea>
              </div>
              <div class="flex justify-end gap-3">
                <button
                    @click="closeAddTaskModal"
                    type="button"
                    class="bg-slate-200 hover:bg-slate-300 dark:bg-slate-700 dark:hover:bg-slate-600 text-slate-700 dark:text-slate-200 px-5 py-2.5 rounded-xl font-semibold transition-all duration-200"
                >
                  Cancel
                </button>
                <button
                    type="submit"
                    class="bg-emerald-600 hover:bg-emerald-700 text-white px-5 py-2.5 rounded-xl font-semibold shadow-md hover:shadow-lg transition-all duration-200"
                >
                  Add Task
                </button>
              </div>
            </form>
          </div>

          <!-- Right Column: Live Preview -->
          <div>
            <div class="sticky top-0">
              <h4 class="text-lg font-bold mb-3 text-slate-900 dark:text-white">Preview</h4>
              <p class="text-xs text-slate-500 dark:text-slate-400 mb-4">This is how your assignment will appear</p>

              <!-- Preview Card (Day View Style) -->
              <div class="bg-white dark:bg-slate-800 rounded-xl p-5 shadow-md border border-slate-200 dark:border-slate-700">
                <div class="flex flex-col justify-between items-start gap-4">
                  <div class="flex-1 w-full">
                    <div class="flex items-start gap-3 mb-3">
                      <div class="w-1 h-12 bg-slate-600 dark:bg-indigo-600 rounded-full flex-shrink-0"></div>
                      <div class="flex-1">
                        <h5 class="text-lg font-bold mb-1.5 text-slate-900 dark:text-white">
                          {{ previewAssignment.section_name }}
                        </h5>
                        <p class="text-slate-600 dark:text-slate-300 text-sm leading-relaxed">
                          {{ truncateText(previewAssignment.short_description, 100) }}
                        </p>
                      </div>
                    </div>
                  </div>
                  <div class="flex flex-col items-end gap-3 w-full">
                    <div class="flex items-center gap-2 text-slate-500 dark:text-slate-400 text-sm">
                      <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                      <span class="font-medium">{{ newTask.date_due ? formatDate(previewAssignment.date_due) : 'No date selected' }}</span>
                    </div>
                    <!-- Status Pills -->
                    <div class="flex gap-2">
                      <button
                          v-for="status in statuses"
                          :key="status.value"
                          disabled
                          :class="[
                          'px-3 py-1.5 rounded-lg text-xs font-semibold transition-all duration-200 cursor-default',
                          previewAssignment.status === status.value
                            ? `${status.bgClass} text-white shadow-sm`
                            : 'bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-400'
                        ]"
                      >
                        {{ status.label }}
                      </button>
                    </div>
                    <!-- Delete Button -->
                    <button
                        disabled
                        class="text-red-600 dark:text-red-400 font-semibold text-xs flex items-center gap-1 cursor-default opacity-70"
                    >
                      <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                      Delete
                    </button>
                  </div>
                </div>
              </div>

              <!-- Optional: Show long description preview if filled -->
              <div v-if="newTask && newTask.long_description && newTask.long_description.trim()" class="mt-4 bg-slate-50 dark:bg-slate-800/50 rounded-xl p-4 border border-slate-200 dark:border-slate-700">
                <h5 class="text-xs font-bold mb-2 text-slate-700 dark:text-slate-300">Description Preview:</h5>
                <p class="text-xs text-slate-600 dark:text-slate-400 whitespace-pre-wrap break-words max-h-32 overflow-y-auto">
                  {{ previewAssignment.long_description }}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </main>
</template>

<script>
import axios from 'axios';
import he from 'he';
import sanitizeHtml from 'sanitize-html';
import Swal from 'sweetalert2';
import { consumeTokenFromUrl, getActiveAuthToken, redirectToLogin as beginLogin } from '@/config/auth';
import { backendUrl } from '@/config/backend';

export default {
  data() {
    return {
      token: null,
      showModal: false,
      showAddTaskModal: false,
      currentAssignment: null,
      newTask: {
        section_name: '',
        short_description: '',
        date_due: '',
        long_description: '',
      },
      showDatePicker: false,
      currentMonth: new Date(),
      assignments: [],
      // Cache for schedules by date (to look up class start times)
      schedulesCache: {},
      scheduleRequests: {},
      schedulePrefetchQueued: false,
      currentDayIndex: 0,
      currentWeekStart: null,
      daysOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
      statuses: [
        {
          value: 'not-started',
          label: 'Not Started',
          activeClass: 'bg-red-700 text-white',
          inactiveClass: 'bg-red-50 text-red-600',
          activeColorClass: 'text-red-500',
          bgClass: 'bg-red-600',
        },
        {
          value: 'in-progress',
          label: 'In Progress',
          activeClass: 'bg-yellow-500 text-white',
          inactiveClass: 'bg-yellow-50 text-yellow-700',
          activeColorClass: 'text-yellow-500',
          bgClass: 'bg-amber-500',
        },
        {
          value: 'complete',
          label: 'Complete',
          activeClass: 'bg-green-700 text-white',
          inactiveClass: 'bg-green-50 text-green-700',
          activeColorClass: 'text-green-500',
          bgClass: 'bg-green-600',
        },
      ],
      isLoading: false,
      isWeekView: true,
      savedScrollY: 0,
    };
  },
  computed: {
    anyModalOpen() {
      return this.showModal || this.showAddTaskModal;
    },
    swalDarkModeConfig() {
      return {
        buttonsStyling: false,
        customClass: {
          popup: 'bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-2xl',
          title: 'text-slate-900 dark:text-white',
          htmlContainer: 'text-slate-700 dark:text-slate-300',
          confirmButton: 'bg-indigo-600 hover:bg-indigo-700 text-white font-semibold py-2 px-4 rounded-lg ml-2',
          cancelButton: 'bg-slate-200 dark:bg-slate-700 hover:bg-slate-300 dark:hover:bg-slate-600 text-slate-900 dark:text-white font-semibold py-2 px-4 rounded-lg mr-2',
          actions: 'gap-2'
        }
      };
    },
    previewAssignment() {
      if (!this.newTask) {
        return {
          section_name: 'Custom Assignment',
          short_description: 'Custom Assignment',
          date_due: new Date().toISOString(),
          long_description: 'No additional details provided.',
          status: 'not-started',
          user_task: true
        };
      }
      return {
        section_name: (this.newTask.section_name || '').trim() || 'Custom Assignment',
        short_description: (this.newTask.short_description || '').trim() || 'Custom Assignment',
        date_due: this.newTask.date_due || new Date().toISOString(),
        long_description: (this.newTask.long_description || '').trim() || 'No additional details provided.',
        status: 'not-started',
        user_task: true
      };
    },
    calendarDays() {
      const year = this.currentMonth.getFullYear();
      const month = this.currentMonth.getMonth();

      // First day of the month
      const firstDay = new Date(year, month, 1);
      // Last day of the month
      const lastDay = new Date(year, month + 1, 0);

      // Get day of week for first day (0 = Sunday, 1 = Monday, etc.)
      let startingDayOfWeek = firstDay.getDay();
      // Convert to Monday-based (0 = Monday, 6 = Sunday)
      startingDayOfWeek = startingDayOfWeek === 0 ? 6 : startingDayOfWeek - 1;

      const daysInMonth = lastDay.getDate();
      const days = [];

      // Add empty slots for days before the first day of month
      for (let i = 0; i < startingDayOfWeek; i++) {
        days.push(null);
      }

      // Add all days in the month
      for (let day = 1; day <= daysInMonth; day++) {
        days.push(new Date(year, month, day));
      }

      return days;
    },
    currentMonthYear() {
      return this.currentMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
    },
    dailyAssignments() {
      const currentDate = this.getDateByDay(this.currentWeekStart, this.currentDayIndex);
      const formattedDate = this.formatApiDate(currentDate);
      return this.assignments.filter((assignment) => {
        const assignmentDate = assignment.date_due.split('T')[0];
        return assignmentDate === formattedDate;
      });
    },
    weeklyAssignments() {
      const assignmentsByDay = {};
      for (let i = 0; i < this.daysOfWeek.length; i++) {
        const dayName = this.daysOfWeek[i];
        const currentDate = this.getDateByDay(this.currentWeekStart, i);
        const formattedDate = this.formatApiDate(currentDate);
        assignmentsByDay[dayName] = this.assignments.filter((assignment) => {
          const assignmentDate = assignment.date_due.split('T')[0];
          return assignmentDate === formattedDate;
        });
      }
      return assignmentsByDay;
    },
  },
  watch: {
    anyModalOpen(isOpen) {
      if (isOpen) this.lockBodyScroll();
      else this.unlockBodyScroll();
    },
  },
  created() {
    this.currentDayIndex = this.getCurrentDayIndex();
    this.currentWeekStart = this.getCurrentWeekStart();
  },
  methods: {
    lockBodyScroll() {
      this.savedScrollY = window.scrollY || window.pageYOffset || 0;
      const body = document.body;
      body.style.position = 'fixed';
      body.style.top = `-${this.savedScrollY}px`;
      body.style.left = '0';
      body.style.right = '0';
      body.style.width = '100%';
      body.style.overflow = 'hidden';
    },
    unlockBodyScroll() {
      const body = document.body;
      body.style.position = '';
      body.style.top = '';
      body.style.left = '';
      body.style.right = '';
      body.style.width = '';
      body.style.overflow = '';
      window.scrollTo(0, this.savedScrollY || 0);
    },
    getCurrentDayIndex() {
      const now = new Date();

      // Get current date/time in Eastern timezone
      const easternFormatter = new Intl.DateTimeFormat('en-US', {
        timeZone: 'America/New_York',
        hour: '2-digit',
        hour12: false,
        weekday: 'short'
      });

      const parts = easternFormatter.formatToParts(now);
      const getPart = (type) => parts.find(p => p.type === type)?.value;

      const hourET = parseInt(getPart('hour'));
      const weekdayStr = getPart('weekday');

      const weekdayMap = { 'Sun': 0, 'Mon': 1, 'Tue': 2, 'Wed': 3, 'Thu': 4, 'Fri': 5, 'Sat': 6 };
      let dayOfWeek = weekdayMap[weekdayStr];

      // If after 4pm Eastern, advance to next day
      if (hourET >= 16) {
        if (dayOfWeek === 5) {
          dayOfWeek = 1; // Friday after 4pm -> Monday
        } else if (dayOfWeek === 6 || dayOfWeek === 0) {
          dayOfWeek = 1; // Weekend -> Monday
        } else {
          dayOfWeek += 1;
        }
      }

      if (dayOfWeek === 6 || dayOfWeek === 0) return 0;
      return dayOfWeek - 1;
    },
    getCurrentWeekStart() {
      const now = new Date();

      // Get current date/time components in Eastern timezone
      const easternFormatter = new Intl.DateTimeFormat('en-US', {
        timeZone: 'America/New_York',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        hour12: false,
        weekday: 'short'
      });

      const parts = easternFormatter.formatToParts(now);
      const getPart = (type) => parts.find(p => p.type === type)?.value;

      const yearET = parseInt(getPart('year'));
      const monthET = parseInt(getPart('month')) - 1; // 0-indexed for Date constructor
      const dayET = parseInt(getPart('day'));
      const hourET = parseInt(getPart('hour'));
      const weekdayStr = getPart('weekday');

      // Map weekday string to number (0=Sun, 1=Mon, ..., 6=Sat)
      const weekdayMap = { 'Sun': 0, 'Mon': 1, 'Tue': 2, 'Wed': 3, 'Thu': 4, 'Fri': 5, 'Sat': 6 };
      const dayOfWeekET = weekdayMap[weekdayStr];

      const isFridayAfter4pm = dayOfWeekET === 5 && hourET >= 16;
      const isWeekend = dayOfWeekET === 6 || dayOfWeekET === 0;

      let targetDay = dayET;

      if (isFridayAfter4pm || isWeekend) {
        const daysUntilNextMonday = (8 - dayOfWeekET) % 7 || 7;
        targetDay += daysUntilNextMonday;
      } else {
        const daysSinceMonday = (dayOfWeekET + 6) % 7;
        targetDay -= daysSinceMonday;
      }

      // Create date at noon UTC to avoid day boundary issues when converting timezones
      // 17:00 UTC = 12:00 noon Eastern (during EST) - safe middle of day
      const weekStartDate = new Date(Date.UTC(yearET, monthET, targetDay, 17, 0, 0, 0));

      return weekStartDate;
    },
    extractToken() {
      const token = consumeTokenFromUrl() || getActiveAuthToken();

      if (token) {
        this.token = token;
      } else {
        beginLogin({ originalUrl: window.location.href });
      }
    },
    isTokenExpired(token) {
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        const expiry = payload.exp;
        const now = Math.floor(new Date().getTime() / 1000);
        return now > expiry;
      } catch (error) {
        console.error('Error decoding token:', error);
        return true;
      }
    },
    async fetchAssignments(showLoading = true) {
      if (!this.token || this.isTokenExpired(this.token)) {
        beginLogin({ originalUrl: window.location.href });
        return;
      }
      try {
        if (showLoading) this.isLoading = true;

        const start_date = this.formatApiDate(this.currentWeekStart);
        const end_date = this.formatApiDate(
            new Date(this.currentWeekStart.getTime() + 6 * 24 * 60 * 60 * 1000)
        );

        const response = await axios.get(backendUrl('/assignments'), {
          params: { start_date, end_date },
          headers: { Authorization: `Bearer ${this.token}` },
        });

        // Clean HTML tags from assignment fields
        this.assignments = response.data.value.map(assignment => ({
          ...assignment,
          short_description: this.cleanAssignmentDescription(assignment.short_description),
          long_description: this.cleanAssignmentDescription(assignment.long_description),
          section_name: this.cleanAssignmentDescription(assignment.section_name)
        }));
      } catch (error) {
        console.error('Error fetching assignments:', error);
        if (error.response && error.response.status === 401) {
          beginLogin({ originalUrl: window.location.href });
        }
      } finally {
        if (showLoading) this.isLoading = false;
        this.$nextTick(() => {
          this.queueSchedulePrefetch();
        });
      }
    },
    nextWeek() {
      // Add 7 days using timestamp math for consistency
      this.currentWeekStart = new Date(this.currentWeekStart.getTime() + 7 * 24 * 60 * 60 * 1000);
      this.currentDayIndex = 0;
      this.fetchAssignments();
    },
    previousWeek() {
      // Subtract 7 days using timestamp math for consistency
      this.currentWeekStart = new Date(this.currentWeekStart.getTime() - 7 * 24 * 60 * 60 * 1000);
      this.currentDayIndex = 0;
      this.fetchAssignments();
    },
    formatDate(dateInput) {
      let date = dateInput;
      if (!(dateInput instanceof Date)) date = new Date(dateInput);

      return date.toLocaleDateString('en-US', {
        month: 'long',
        day: 'numeric',
        year: 'numeric',
        timeZone: 'America/New_York'  // Force Eastern timezone display
      });
    },
    formatModalDueDate(dateString) {
      if (!dateString) return '';
      // Parse the date string (YYYY-MM-DD or ISO format)
      const datePart = dateString.split('T')[0];
      const [year, month, day] = datePart.split('-').map(Number);
      const date = new Date(year, month - 1, day);

      return date.toLocaleDateString('en-US', {
        weekday: 'long',
        month: 'long',
        day: 'numeric',
        year: 'numeric',
        timeZone: 'America/New_York'
      });
    },
    formatTime(timeString) {
      // Parse ISO string and display in Eastern timezone
      const date = new Date(timeString);
      return date.toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,
        timeZone: 'America/New_York'  // Force Eastern timezone display
      });
    },
    formatApiDate(date) {
      // Format date in Eastern timezone for API calls
      const easternFormatter = new Intl.DateTimeFormat('en-CA', {
        timeZone: 'America/New_York',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      });
      // en-CA gives YYYY-MM-DD format
      return easternFormatter.format(date);
    },
    getDateByDay(weekStart, dayIndex) {
      // Add days to the week start date
      // weekStart is stored at 17:00 UTC (noon Eastern), so adding days keeps us at same time
      const date = new Date(weekStart.getTime() + dayIndex * 24 * 60 * 60 * 1000);
      return date;
    },
    cleanAssignmentDescription(html) {
      if (html == null) return '';
      let withLineBreaks = String(html).replace(/<br\s*\/?>/gi, '\n').replace(/<\/?p>/gi, '\n');
      for (let i = 0; i < 5; i += 1) {
        const next = he.decode(withLineBreaks);
        if (next === withLineBreaks) break;
        withLineBreaks = next;
      }
      const cleanText = sanitizeHtml(withLineBreaks, { allowedTags: [], allowedAttributes: {} });
      return he.decode(cleanText).trim();
    },
    queueSchedulePrefetch() {
      if (this.schedulePrefetchQueued) return;
      this.schedulePrefetchQueued = true;

      const runPrefetch = () => {
        this.schedulePrefetchQueued = false;
        this.prefetchSchedulesForAssignments();
      };

      if (typeof window !== 'undefined' && 'requestIdleCallback' in window) {
        window.requestIdleCallback(runPrefetch, { timeout: 1500 });
      } else {
        setTimeout(runPrefetch, 0);
      }
    },
    prefetchSchedulesForAssignments() {
      if (!this.assignments.length) return;

      const uniqueDates = new Set();
      for (const assignment of this.assignments) {
        const dateStr = assignment?.date_due?.split('T')[0];
        if (dateStr) uniqueDates.add(dateStr);
      }

      for (const dateStr of uniqueDates) {
        if (!Object.prototype.hasOwnProperty.call(this.schedulesCache, dateStr)) {
          this.fetchScheduleForDate(dateStr);
        }
      }
    },
    async fetchScheduleForDate(dateStr) {
      // Check cache first
      if (Object.prototype.hasOwnProperty.call(this.schedulesCache, dateStr)) {
        return this.schedulesCache[dateStr];
      }

      if (this.scheduleRequests[dateStr]) {
        return this.scheduleRequests[dateStr];
      }

      // Fetch schedule for the specific date
      const request = axios.get(backendUrl('/schedule'), {
        params: { start_date: dateStr, end_date: dateStr },
        headers: { Authorization: `Bearer ${this.token}` },
      })
          .then((response) => {
            this.schedulesCache[dateStr] = response.data.value || [];
            return this.schedulesCache[dateStr];
          })
          .catch((error) => {
            console.error('Error fetching schedule for date:', dateStr, error);
            return [];
          })
          .finally(() => {
            delete this.scheduleRequests[dateStr];
          });

      this.scheduleRequests[dateStr] = request;
      return request;
    },
    getClassStartTimeFromSchedules(schedules, sectionName) {
      // Try to match the assignment's section_name to a schedule's course_title
      // Use case-insensitive matching and handle partial matches
      const normalizedSectionName = sectionName.toLowerCase().trim();

      for (const schedule of schedules) {
        const normalizedCourseTitle = (schedule.course_title || '').toLowerCase().trim();
        // Check for exact match or if one contains the other
        if (normalizedCourseTitle === normalizedSectionName ||
            normalizedCourseTitle.includes(normalizedSectionName) ||
            normalizedSectionName.includes(normalizedCourseTitle)) {
          return this.formatTime(schedule.start_time);
        }
      }
      return null;
    },
    async openModal(assignment) {
      const cleanedDescription = this.cleanAssignmentDescription(assignment.long_description);
      const assignmentData = { ...assignment, long_description: cleanedDescription, classStartTime: null };

      this.currentAssignment = assignmentData;
      this.showModal = true;

      // Get the due date in YYYY-MM-DD format
      const dueDateStr = assignment.date_due.split('T')[0];

      // Try to get class start time from cached schedules or fetch if needed
      const schedules = await this.fetchScheduleForDate(dueDateStr);
      const classStartTime = this.getClassStartTimeFromSchedules(schedules, assignment.section_name);

      if (this.currentAssignment &&
          this.currentAssignment.assignment_id === assignment.assignment_id) {
        this.currentAssignment = { ...this.currentAssignment, classStartTime };
      }
    },
    closeModal() {
      this.showModal = false;
      this.currentAssignment = null;
    },
    openAddTaskModal() {
      this.showAddTaskModal = true;
    },
    closeAddTaskModal() {
      this.showAddTaskModal = false;
      this.showDatePicker = false;
      this.currentMonth = new Date();
      this.newTask = { section_name: '', short_description: '', date_due: '', long_description: '' };
    },
    toggleDatePicker() {
      this.showDatePicker = !this.showDatePicker;
      if (this.showDatePicker) {
        // Reset to current month when opening
        this.currentMonth = new Date();
      }
    },
    previousMonth() {
      this.currentMonth = new Date(this.currentMonth.getFullYear(), this.currentMonth.getMonth() - 1, 1);
    },
    nextMonth() {
      this.currentMonth = new Date(this.currentMonth.getFullYear(), this.currentMonth.getMonth() + 1, 1);
    },
    selectDate(date) {
      if (!date) return;

      // Check if it's a weekend
      const dayOfWeek = date.getDay();
      if (dayOfWeek === 0 || dayOfWeek === 6) {
        return; // Don't allow weekend selection
      }

      // Check if it's in the past
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      date.setHours(0, 0, 0, 0);

      if (date < today) {
        return; // Don't allow past dates
      }

      // Format date as YYYY-MM-DD
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      this.newTask.date_due = `${year}-${month}-${day}`;
      this.showDatePicker = false;
    },
    isDateDisabled(date) {
      if (!date) return false;

      const dayOfWeek = date.getDay();
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const checkDate = new Date(date);
      checkDate.setHours(0, 0, 0, 0);

      // Disable weekends and past dates
      return dayOfWeek === 0 || dayOfWeek === 6 || checkDate < today;
    },
    isDateSelected(date) {
      if (!date || !this.newTask.date_due) return false;

      const selectedDate = new Date(this.newTask.date_due);
      return (
        date.getFullYear() === selectedDate.getFullYear() &&
        date.getMonth() === selectedDate.getMonth() &&
        date.getDate() === selectedDate.getDate()
      );
    },
    async addTask() {
      // Validate that a date is selected
      if (!this.newTask.date_due) {
        Swal.fire({
          ...this.swalDarkModeConfig,
          icon: 'warning',
          title: 'Missing Date',
          text: 'Please select a due date for your assignment.'
        });
        return;
      }

      // Validate that at least one text field is filled
      const hasContent = this.newTask.section_name.trim() ||
          this.newTask.short_description.trim() ||
          this.newTask.long_description.trim();

      if (!hasContent) {
        Swal.fire({
          ...this.swalDarkModeConfig,
          icon: 'warning',
          title: 'Missing Information',
          text: 'Please fill in at least one field (Section, Title, or Description).'
        });
        return;
      }

      // Set defaults for empty fields
      const taskData = {
        section_name: this.newTask.section_name.trim() || 'Custom Assignment',
        short_description: this.newTask.short_description.trim() || 'Custom Assignment',
        date_due: this.newTask.date_due,
        long_description: this.newTask.long_description.trim() || 'No additional details provided.',
      };

      const dateParts = taskData.date_due.split('-');
      const assignmentDate = new Date(
          parseInt(dateParts[0], 10),
          parseInt(dateParts[1], 10) - 1,
          parseInt(dateParts[2], 10)
      );

      const today = new Date();
      today.setHours(0, 0, 0, 0);
      assignmentDate.setHours(0, 0, 0, 0);

      if (assignmentDate < today) {
        Swal.fire({
          ...this.swalDarkModeConfig,
          icon: 'error',
          title: 'Invalid Date',
          text: 'You cannot select a past date.'
        });
        return;
      }

      const dayOfWeek = assignmentDate.getDay();
      if (dayOfWeek === 0 || dayOfWeek === 6) {
        Swal.fire({
          ...this.swalDarkModeConfig,
          icon: 'error',
          title: 'Invalid Date',
          text: 'Assignments cannot be submitted on weekends.'
        });
        return;
      }

      try {
        const response = await axios.post(
            backendUrl('/custom_assignments'),
            taskData,
            { headers: { Authorization: `Bearer ${this.token}` } }
        );

        const taskToAdd = response.data;
        taskToAdd.user_task = true;
        taskToAdd.status = 'not-started';
        this.assignments.push(taskToAdd);

        this.closeAddTaskModal();
      } catch (error) {
        console.error('Error adding custom task:', error);
        Swal.fire({
          ...this.swalDarkModeConfig,
          icon: 'error',
          title: 'Error',
          text: 'An error occurred while adding the task. Please try again.'
        });
      }
    },
    async deleteAssignment(assignment) {
      const result = await Swal.fire({
        ...this.swalDarkModeConfig,
        icon: 'warning',
        title: 'Delete Assignment?',
        text: 'Are you sure you want to delete this assignment?',
        showCancelButton: true,
        confirmButtonText: 'Yes, delete it',
        cancelButtonText: 'Cancel',
        customClass: {
          ...this.swalDarkModeConfig.customClass,
          confirmButton: 'bg-red-600 hover:bg-red-700 text-white font-semibold py-2 px-4 rounded-lg'
        }
      });

      if (!result.isConfirmed) return;

      try {
        await axios.delete(
            backendUrl(`/custom_assignments/${assignment.assignment_id}`),
            { headers: { Authorization: `Bearer ${this.token}` } }
        );

        this.assignments = this.assignments.filter(
            (a) => a.assignment_id !== assignment.assignment_id
        );
      } catch (error) {
        console.error('Error deleting assignment:', error);
        Swal.fire({
          ...this.swalDarkModeConfig,
          icon: 'error',
          title: 'Error',
          text: 'An error occurred while deleting the assignment. Please try again.'
        });
      }
    },
    async updateStatus(assignment, status) {
      assignment.status = status;

      try {
        await axios.post(
            backendUrl('/assignment_statuses'),
            { assignment_id: assignment.assignment_id, status },
            { headers: { Authorization: `Bearer ${this.token}` } }
        );

        await this.fetchAssignments(false);
      } catch (error) {
        console.error('Error updating assignment status:', error);
      }
    },
    truncateText(text, length) {
      if (!text) return '';
      if (text.length <= length) return text;
      return text.substring(0, length) + '...';
    },
    pluralize(count, singularVerbPhrase, pluralVerbPhrase) {
      return count === 1 ? singularVerbPhrase : pluralVerbPhrase;
    },
    handleClickOutside(event) {
      // Close date picker if clicking outside of it
      if (this.showDatePicker) {
        const datePickerElement = event.target.closest('.relative');
        if (!datePickerElement) {
          this.showDatePicker = false;
        }
      }
    },
  },
  mounted() {
    this.extractToken();
    this.fetchAssignments();
    // Add click outside handler for date picker
    document.addEventListener('click', this.handleClickOutside);
  },
  beforeUnmount() {
    if (this.anyModalOpen) this.unlockBodyScroll();
    // Remove click outside handler
    document.removeEventListener('click', this.handleClickOutside);
  },
};
</script>

<style scoped>
@import url('https://cdnjs.cloudflare.com/ajax/libs/font-awesome/5.15.3/css/all.min.css');

/* Scrollbar Styling */
::-webkit-scrollbar {
  width: 8px;
}

::-webkit-scrollbar-track {
  background: transparent;
}

::-webkit-scrollbar-thumb {
  background: #64748b;
  border-radius: 10px;
}

::-webkit-scrollbar-thumb:hover {
  background: #475569;
}

.dark::-webkit-scrollbar-thumb {
  background: #818cf8;
}

.dark::-webkit-scrollbar-thumb:hover {
  background: #6366f1;
}

/* Make the date picker icon visible in dark mode */
.dark input[type="date"]::-webkit-calendar-picker-indicator {
  filter: invert(1);
}

/* Modal description scrollbar */
.desc-scroll::-webkit-scrollbar {
  width: 6px;
}
.desc-scroll::-webkit-scrollbar-thumb {
  background: #64748b;
  border-radius: 4px;
}
.dark .desc-scroll::-webkit-scrollbar-thumb {
  background: #818cf8;
}

.desc-scroll {
  -webkit-overflow-scrolling: touch;
  overscroll-behavior: contain;
  touch-action: pan-y;
}
</style>
